import { useCallback, useEffect, useMemo } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import { GlobalBannerDynamic } from '@frontend/global-info-banner';
import { Trans, useTranslation } from '@frontend/i18n';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { useNavigate } from '@frontend/weave-router';
import { InlineNotification, Text } from '@frontend/design-system';
import { CALL_INTEL_BASE_URL, CALL_RECORDING_SETTINGS } from '../constants';
import { useBackfillBatch1BannerVisibility } from '../hooks/use-backfill-batch1-banner-visibility';
import { useBackfillBatch2BannerVisibility } from '../hooks/use-backfill-batch2-banner-visibility';
import { useBackfillRecordingsBannerVisibility } from '../hooks/use-backfill-recordings-banner-visibility';
import { useCallIntelTrial } from '../hooks/use-call-intel-trial';
import { backfillPendoEvents, trackBackfillEvent } from '../hooks/use-tracking';

export const Batch1ReadyBanner = () => {
  const { t } = useTranslation('call-intelligence');
  const navigate = useNavigate();
  const { dismiss, isOnCallIntelligenceRoute, isVisible } = useBackfillBatch1BannerVisibility();

  useEffect(() => {
    if (isVisible) {
      trackBackfillEvent(backfillPendoEvents.batch1BannerSeen);
    }
  }, [isVisible]);

  const handleDismiss = useCallback(() => {
    dismiss({ dismissedAt: Date.now() });
  }, [dismiss]);

  const action = useMemo(
    () =>
      isOnCallIntelligenceRoute
        ? undefined
        : {
            label: t('Go to Call Intelligence'),
            onClick: () => {
              dismiss({ permanentlyDismissed: true });
              navigate({ to: CALL_INTEL_BASE_URL });
            },
          },
    [dismiss, isOnCallIntelligenceRoute, navigate, t]
  );

  if (!isVisible) return null;

  return (
    <GlobalBannerDynamic
      type='premium'
      id='call-intel-trial-batch1-ready'
      title={t('Start Exploring Your Call Intelligence Free Trial!')}
      message={t('We’ve finished analyzing your first set of calls to give you quick access to your free trial.')}
      isDismissible
      onDismiss={handleDismiss}
      action={action}
      trackingId={backfillPendoEvents.batch1BannerCtaClicked}
      dismissBtnTrackingId={backfillPendoEvents.batch1BannerDismissed}
    />
  );
};

export const FullyCompleteBanner = () => {
  const { t } = useTranslation('call-intelligence');
  const navigate = useNavigate();
  const { dismiss, isOnCallIntelligenceRoute, isVisible, hasPartialRecordingHistory } =
    useBackfillBatch2BannerVisibility();

  useEffect(() => {
    if (isVisible) {
      trackBackfillEvent(backfillPendoEvents.fullyCompleteBannerSeen);
    }
  }, [isVisible]);

  const handleDismiss = useCallback(() => {
    dismiss({ dismissedAt: Date.now() });
  }, [dismiss]);

  const action = useMemo(
    () =>
      isOnCallIntelligenceRoute
        ? undefined
        : {
            label: t('Go to Call Intelligence'),
            onClick: () => {
              dismiss({ permanentlyDismissed: true });
              navigate({ to: CALL_INTEL_BASE_URL });
            },
          },
    [dismiss, isOnCallIntelligenceRoute, navigate, t]
  );

  if (!isVisible) return null;

  return (
    <GlobalBannerDynamic
      type='premium'
      id='call-intel-trial-fully-complete'
      title={t('Your Call Intelligence Insights are Ready!')}
      message={
        hasPartialRecordingHistory ? (
          t(
            'Your call insights are ready. Dive into patient needs, outcomes, and next steps to make every conversation count.'
          )
        ) : (
          <Trans t={t} components={{ br: <br /> }}>
            {
              'Your last 30 days of call insights are ready so you can start seeing trends and opportunities. Dive into<br />patient needs, outcomes, and next steps to make every conversation count.'
            }
          </Trans>
        )
      }
      isDismissible
      onDismiss={handleDismiss}
      action={action}
      trackingId={backfillPendoEvents.fullyCompleteBannerCtaClicked}
      dismissBtnTrackingId={backfillPendoEvents.fullyCompleteBannerDismissed}
    />
  );
};

export const RecordingsDisabledBanner = () => {
  const { t } = useTranslation('call-intelligence');
  const { navigate } = useSettingsNavigate();
  const { dismiss, isVisible } = useBackfillRecordingsBannerVisibility();

  useEffect(() => {
    if (isVisible) {
      trackBackfillEvent(backfillPendoEvents.recordingsDisabledBannerSeen);
    }
  }, [isVisible]);

  const handleDismiss = useCallback(() => {
    dismiss();
  }, [dismiss]);

  const action = useMemo(
    () => ({
      label: t('Enable Call Recordings'),
      onClick: () => {
        navigate({ to: CALL_RECORDING_SETTINGS });
      },
    }),
    [navigate, t]
  );

  if (!isVisible) return null;

  return (
    <GlobalBannerDynamic
      type='error'
      id='call-intel-trial-recordings-disabled'
      title={t('Call Recordings are Disabled for Your Call Intelligence Free Trial')}
      message={
        <Trans t={t} components={{ br: <br /> }}>
          {
            'We are unable to analyze any new calls for your free trial. Please enable call recordings so that<br />Call Intelligence can transcribe and analyze new calls and prepare your dashboard.'
          }
        </Trans>
      }
      isDismissible
      onDismiss={handleDismiss}
      action={action}
      trackingId={backfillPendoEvents.recordingsDisabledBannerCtaClicked}
      dismissBtnTrackingId={backfillPendoEvents.recordingsDisabledBannerDismissed}
    />
  );
};

export const TrialBackfillInProgressBanner = ({
  locationIds,
  customStyles,
}: {
  locationIds?: string[];
  customStyles?: Interpolation<Theme>;
}) => {
  const { t } = useTranslation('call-intelligence');
  const { isFirstBatchReady, isFullyComplete, hasTrialAndSubscribedLocations } = useCallIntelTrial({ locationIds });

  if (!isFirstBatchReady || isFullyComplete || hasTrialAndSubscribedLocations) return null;

  return (
    <InlineNotification size='medium' status='premium' customStyles={customStyles}>
      <Text size='medium'>
        {t('We’re still preparing insights from your last 30 days of calls. We’ll notify you when they’re ready.')}
      </Text>
    </InlineNotification>
  );
};

export const TrialActivatedLocationsBanner = ({
  locationIds,
  customStyles,
}: {
  locationIds?: string[];
  customStyles?: Interpolation<Theme>;
}) => {
  const { t } = useTranslation('call-intelligence');
  const { hasTrialAndSubscribedLocations, isFirstBatchReady, trialProcessingLocationIds } = useCallIntelTrial({
    locationIds,
  });

  if (!hasTrialAndSubscribedLocations || isFirstBatchReady || !trialProcessingLocationIds.length) return null;

  return (
    <InlineNotification size='medium' status='premium' customStyles={customStyles}>
      <Text size='medium'>
        {t(
          "Your free trial is activated for {{count}} locations. Insights for these locations will appear on your dashboard when they're ready.",
          { count: trialProcessingLocationIds.length }
        )}
      </Text>
    </InlineNotification>
  );
};
